import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScanLine, Camera, CameraOff, CheckCircle, XCircle, Search, QrCode } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";

interface ScanResult {
  id: string;
  name: string;
  type: "child" | "staff";
  status: "present" | "late";
  time: string;
}

export default function QRScanner() {
  const { profile } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [lastScan, setLastScan] = useState<ScanResult | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const readerDivRef = useRef<HTMLDivElement>(null);
  const scannerContainerId = useRef(`qr-reader-${Date.now()}`).current;

  useEffect(() => {
    return () => {
      if (scannerRef.current?.isScanning) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startScanning = async () => {
    try {
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;
      
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        onScanSuccess,
        () => {} // ignore scan failures
      );
      setScanning(true);
    } catch (err: any) {
      console.error("Camera error:", err);
      toast.error("لا يمكن الوصول للكاميرا. تأكد من منح الإذن.");
    }
  };

  const stopScanning = async () => {
    if (scannerRef.current?.isScanning) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch {}
    }
    scannerRef.current = null;
    setScanning(false);
  };

  const onScanSuccess = async (decodedText: string) => {
    // Prevent duplicate scans within 3 seconds
    const now = Date.now();
    if (lastScan && lastScan.id === decodedText && now - parseInt(lastScan.time) < 3000) return;
    
    await processCode(decodedText);
  };

  const processCode = async (code: string) => {
    if (!profile?.nursery_id) return;

    // Try to find child or staff by ID
    const [childRes, staffRes] = await Promise.all([
      supabase.from("children").select("id, full_name").eq("id", code).eq("nursery_id", profile.nursery_id).maybeSingle(),
      supabase.from("staff").select("id, full_name").eq("id", code).eq("nursery_id", profile.nursery_id).maybeSingle(),
    ]);

    const person = childRes.data || staffRes.data;
    const type = childRes.data ? "child" : "staff";

    if (!person) {
      toast.error("لم يتم العثور على هذا الكود في النظام");
      return;
    }

    // Check if already checked in today
    const today = format(new Date(), "yyyy-MM-dd");
    const { data: existing } = await supabase
      .from("attendance")
      .select("id, check_in_time, check_out_time")
      .eq("attendee_id", person.id)
      .eq("date", today)
      .eq("nursery_id", profile.nursery_id)
      .maybeSingle();

    const now = new Date();
    const timeStr = format(now, "HH:mm:ss");

    if (existing) {
      if (!existing.check_out_time) {
        // Check out
        await supabase.from("attendance").update({ check_out_time: now.toISOString() }).eq("id", existing.id);
        
        const result: ScanResult = {
          id: person.id,
          name: person.full_name,
          type,
          status: "present",
          time: format(now, "hh:mm a"),
        };
        setScanResults((prev) => [{ ...result, time: `انصراف ${format(now, "hh:mm a")}` }, ...prev]);
        setLastScan({ ...result, time: Date.now().toString() });
        toast.success(`تم تسجيل انصراف ${person.full_name}`);
      } else {
        toast.info(`${person.full_name} سجّل الحضور والانصراف بالفعل اليوم`);
      }
    } else {
      // Check in
      const hour = now.getHours();
      const status = hour >= 9 ? "late" : "present"; // Late after 9 AM

      await supabase.from("attendance").insert({
        nursery_id: profile.nursery_id,
        attendee_id: person.id,
        attendee_type: type,
        date: today,
        status,
        method: "qr_scan",
        check_in_time: now.toISOString(),
      });

      const result: ScanResult = {
        id: person.id,
        name: person.full_name,
        type,
        status,
        time: format(now, "hh:mm a"),
      };
      setScanResults((prev) => [result, ...prev]);
      setLastScan({ ...result, time: Date.now().toString() });
      toast.success(`تم تسجيل حضور ${person.full_name} - ${status === "late" ? "متأخر" : "حاضر"}`);
    }
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualCode.trim()) {
      processCode(manualCode.trim());
      setManualCode("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Scanner */}
        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-primary" />
              ماسح QR / باركود
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              id={scannerContainerId}
              ref={readerDivRef}
              className="w-full rounded-xl overflow-hidden bg-muted/30 min-h-[280px] flex items-center justify-center"
            >
              {!scanning && (
                <div className="text-center p-6 space-y-3">
                  <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                    <QrCode className="w-10 h-10 text-primary" />
                  </div>
                  <p className="text-muted-foreground text-sm">اضغط لبدء المسح</p>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {!scanning ? (
                <Button onClick={startScanning} className="flex-1 gradient-hero border-0">
                  <Camera className="w-4 h-4 ml-2" />
                  بدء المسح
                </Button>
              ) : (
                <Button onClick={stopScanning} variant="destructive" className="flex-1">
                  <CameraOff className="w-4 h-4 ml-2" />
                  إيقاف المسح
                </Button>
              )}
            </div>

            {/* Manual Code Entry */}
            <div className="border-t pt-4">
              <p className="text-xs text-muted-foreground mb-2">أو أدخل الكود يدوياً:</p>
              <form onSubmit={handleManualSubmit} className="flex gap-2">
                <Input
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="أدخل كود البطاقة..."
                  dir="ltr"
                  className="flex-1"
                />
                <Button type="submit" variant="outline" size="icon">
                  <Search className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>

        {/* Scan Log */}
        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">سجل التسجيل اليوم</CardTitle>
              <Badge variant="outline">{scanResults.length} تسجيل</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {scanResults.length === 0 ? (
              <div className="py-12 text-center">
                <ScanLine className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-muted-foreground text-sm">لم يتم مسح أي بطاقات بعد</p>
              </div>
            ) : (
              <div className="divide-y max-h-[400px] overflow-y-auto">
                <AnimatePresence>
                  {scanResults.map((r, i) => (
                    <motion.div
                      key={`${r.id}-${i}`}
                      initial={{ opacity: 0, y: -20, backgroundColor: "hsl(var(--primary) / 0.1)" }}
                      animate={{ opacity: 1, y: 0, backgroundColor: "transparent" }}
                      transition={{ duration: 0.4 }}
                      className="p-3 flex items-center gap-3"
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                        r.status === "present" ? "bg-primary/10" : "bg-yellow-100"
                      }`}>
                        {r.status === "present" ? (
                          <CheckCircle className="w-4 h-4 text-primary" />
                        ) : (
                          <XCircle className="w-4 h-4 text-yellow-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{r.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.type === "child" ? "طفل" : "موظف"}
                        </p>
                      </div>
                      <div className="text-left shrink-0">
                        <p className="text-xs font-medium text-foreground">{r.time}</p>
                        <Badge
                          variant={r.status === "present" ? "default" : "secondary"}
                          className="text-[10px]"
                        >
                          {r.status === "present" ? "حاضر" : "متأخر"}
                        </Badge>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* QR Code Generator Info */}
      <Card className="shadow-card border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <QrCode className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground text-sm">كيفية الاستخدام</p>
              <p className="text-xs text-muted-foreground mt-1">
                يمكنك طباعة بطاقات QR لكل طفل أو موظف من صفحة الأطفال أو الموظفين. عند مسح البطاقة بالكاميرا يتم تسجيل الحضور تلقائياً.
                المسح الأول = تسجيل حضور، المسح الثاني = تسجيل انصراف.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
